import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "primereact/card";
import { TabView, TabPanel } from "primereact/tabview";
import { ProgressSpinner } from "primereact/progressspinner";
import ChildSwitcher from "./ChildSwitcher";
import ChildOverviewTab from "./ChildOverviewTab";
import ChildAttendanceTab from "./ChildAttendanceTab";
import ChildAssignmentsTab from "./ChildAssignmentsTab";
import ChildResultsTab from "./ChildResultsTab";
import ChildFeesTab from "./ChildFeesTab";
import ChildLeavesTab from "./ChildLeavesTab";
import ChildScheduleTab from "./ChildScheduleTab";
import { getChildren } from "../../api/parentDashboardApi";
import "./Parent.scss";

const ParentDashboard = () => {
  const [children, setChildren] = useState([]);
  const [activeChildId, setActiveChildId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab");
  const tabMap = ["overview", "attendance", "assignments", "results", "fees", "leaves", "schedule"];
  const [tabIndex, setTabIndex] = useState(
    initialTab && tabMap.includes(initialTab) ? tabMap.indexOf(initialTab) : 0
  );

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await getChildren();
        if (data?.success) {
          setChildren(data.items || []);
          if (data.items?.[0]) setActiveChildId(data.items[0].id);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const activeChild = children.find((c) => c.id === activeChildId);

  const handleTabChange = (e) => {
    setTabIndex(e.index);
    setSearchParams({ tab: tabMap[e.index] });
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "3rem" }}>
        <ProgressSpinner />
      </div>
    );
  }

  if (children.length === 0) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "3rem" }}>
          <i className="pi pi-info-circle" style={{ fontSize: "3rem", opacity: 0.4 }}></i>
          <h3>No children linked to your account</h3>
          <p className="opacity-70">Please contact the school administration.</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="parent-dashboard">
      <Card className="mb-3">
        <div className="flex align-items-center gap-2 mb-2">
          <i className="pi pi-users text-primary" />
          <span className="font-semibold text-color-secondary text-sm">
            {children.length > 1 ? "Select a child to view their details:" : "Viewing details for:"}
          </span>
        </div>
        <ChildSwitcher items={children} activeId={activeChildId} onChange={setActiveChildId} />
      </Card>

      <Card>
        {activeChild && (
          <div className="flex align-items-center gap-2 mb-3">
            <i className="pi pi-user text-primary" />
            <span className="font-semibold">{activeChild.full_name}</span>
            {activeChild.currentGrade?.name && (
              <span className="text-color-secondary text-sm">· {activeChild.currentGrade.name}</span>
            )}
          </div>
        )}

        {activeChild && (
          <TabView activeIndex={tabIndex} onTabChange={handleTabChange}>
            <TabPanel header="Overview" leftIcon="pi pi-th-large mr-2">
              <ChildOverviewTab child={activeChild} />
            </TabPanel>
            <TabPanel header="Attendance" leftIcon="pi pi-check-square mr-2">
              <ChildAttendanceTab child={activeChild} />
            </TabPanel>
            <TabPanel header="Assignments" leftIcon="pi pi-file-edit mr-2">
              <ChildAssignmentsTab child={activeChild} />
            </TabPanel>
            <TabPanel header="Results" leftIcon="pi pi-chart-line mr-2">
              <ChildResultsTab child={activeChild} />
            </TabPanel>
            <TabPanel header="Fees" leftIcon="pi pi-wallet mr-2">
              <ChildFeesTab child={activeChild} />
            </TabPanel>
            <TabPanel header="Leaves" leftIcon="pi pi-calendar-minus mr-2">
              <ChildLeavesTab child={activeChild} />
            </TabPanel>
            <TabPanel header="Schedule" leftIcon="pi pi-calendar mr-2">
              <ChildScheduleTab child={activeChild} />
            </TabPanel>
          </TabView>
        )}
      </Card>
    </div>
  );
};


export default ParentDashboard;
